/* eslint-disable guard-for-in */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactPaginate from 'react-paginate';
import moment from 'moment';
import {
  ArrowDownTrayIcon,
  ArrowTopRightOnSquareIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CreditCardIcon
} from '@heroicons/react/24/outline';

import api from 'api';
import LoadingState from 'components/LoadingState';
import SettingsPage from 'routes/settings/SettingsPage';
import { logAndCaptureCriticalError, logAndCaptureException } from 'utils';
import { getFirebaseContext } from 'utils/firebase';
import { ColumnButton } from 'lib/components/ColumnButton';
import { Alert } from 'lib/components/Alert';
import { unixTimeStampToNewspaperTimezoneDate } from 'lib/helpers';
import { Product } from 'lib/enums';
import { EPayoutData, exists } from 'lib/types';
import { PayoutGroup } from 'lib/types/payout';
import { makeCsvContent, PAYOUT_CSV_HEADERS } from 'lib/utils/csv';
import { downloadFileContentsInBrowser } from 'lib/frontend/utils/browser';
import { ColumnService } from 'lib/services/directory';
import { getModelFromSnapshot } from 'lib/model';
import { OrganizationModel } from 'lib/model/objects/organizationModel';
import {
  getNewspaperErrorInfoForNewspaperIdWithDefaults,
  missingAccountIdError
} from 'lib/errors/templateErrors';
import { useAppSelector } from 'redux/hooks';
import { selectActiveOrganization } from 'redux/auth';

import { ENOTICE_ACCOUNT_ID } from '../../../../constants';
import SettingsHeader from '../../SettingsHeader';
import PayoutGroupRows from './PayoutGroupRows';
import PayoutsFilterDialog from './PayoutsFilterDialog';
import PayoutsDownloadModal from './PayoutsDownloadModal';
import {
  buildPayoutGroupRows,
  filterPayoutsByAdType,
  payoutHasBalanceTransactionData
} from './payoutTableData';
import {
  ALL_PRODUCT_TYPES_STRING,
  PayoutsFilterValue
} from './payoutFilterTypes';

const PAGE_SIZE = 10;

function PayoutTableHeader({
  header,
  width
}: {
  header?: string;
  width?: string;
}) {
  return (
    <th
      className={`py-3 ${
        width && `w-${width}`
      } bg-column-gray-25 text-left text-xs leading-4 font-medium text-column-gray-400 uppercase tracking-wider`}
    >
      {header}
    </th>
  );
}

export function StripePayouts() {
  const activeOrganization = useAppSelector(selectActiveOrganization);
  const [payoutsLoading, setPayoutsLoading] = useState(true);
  const [payoutGroups, setPayoutGroups] = useState<PayoutGroup[]>([]);
  const [stripeLoginLink, setLoginLink] = useState<string | null>();
  const [stripeInvoicesLink, setStripeInvoicesLink] = useState('');
  const [payouts, setPayouts] = useState<EPayoutData[]>([]);
  const [openDownloadModal, setOpenDownloadModal] = useState(false);
  const [pageNumber, setPageNumber] = useState(0);
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [productsActive, setProductsActive] = useState({
    [Product.Notice]: true,
    [Product.Classified]: false,
    [Product.Obituary]: false
  });
  const [userError, setUserError] = useState('');
  const [missingAccountId, setMissingAccountId] = useState(false);
  const [payoutsFilterValue, setPayoutsFilterValue] =
    useState<PayoutsFilterValue>({
      adType: ALL_PRODUCT_TYPES_STRING
    });

  // Balance transactions for a payout are immutable, so cache them by payout id
  // once fetched. This lets us paginate back and forth without re-fetching and,
  // critically, without losing detail data for pages we already loaded.
  const balanceTransactionCacheRef = useRef<Record<string, EPayoutData>>({});
  // Guards against out-of-order responses when the page/filter changes rapidly:
  // only the most recent page build is allowed to commit its results.
  const pageBuildRequestRef = useRef(0);

  const filteredPayouts = useMemo(
    () => filterPayoutsByAdType(payouts, payoutsFilterValue.adType),
    [payouts, payoutsFilterValue.adType]
  );

  const pageStartIndex = pageNumber * PAGE_SIZE;

  const loadStripeLoginLink = async () => {
    if (!exists(activeOrganization)) {
      return;
    }

    const { accountId } = activeOrganization.data();

    if (!accountId || accountId === ENOTICE_ACCOUNT_ID) {
      setUserError(
        'Payouts cannot be downloaded at this time. We have been notified of this issue and are working to resolve it.'
      );
      const paperInfo = await getNewspaperErrorInfoForNewspaperIdWithDefaults(
        getFirebaseContext(),
        activeOrganization.id
      );
      const err = missingAccountIdError(paperInfo);
      logAndCaptureCriticalError(
        ColumnService.CSMS,
        err,
        'Organization does not have a Stripe Connect ID set in the stripe payouts page',
        {
          organizationId: activeOrganization.id,
          accountId
        }
      );
      setMissingAccountId(true);
      return;
    }
    try {
      const { response: url, error: loginLinkError } = await api.safePost(
        'payments/stripe-login-link',
        { accountId }
      );
      if (loginLinkError) {
        throw loginLinkError;
      }
      setLoginLink(url);
    } catch (err) {
      logAndCaptureException(
        ColumnService.PAYMENTS,
        err,
        'Unable to create login link',
        { accountId }
      );
    }
  };

  const loadStripeInvoicesLink = async () => {
    // Note: this will not be visible if the setting is inherited from the
    // parent org.
    const enabled = activeOrganization?.data()?.payColumnInvoices?.enabled;
    const stripeCustomerId =
      activeOrganization?.data()?.payColumnInvoices?.stripeCustomerId;

    if (!enabled || !stripeCustomerId) {
      return;
    }

    try {
      const { url } = await api.post('subscription/stripe-billing-session', {
        customerId: stripeCustomerId,
        returnUrl: window.location.href,
        configuration: 'organization_invoices'
      });
      setStripeInvoicesLink(url);
    } catch (e) {}
  };

  const downloadCSV = async (payoutId: string) => {
    if (!exists(activeOrganization) || missingAccountId) {
      return;
    }

    const relevantPayout = payouts.find(payout => payout.id === payoutId);
    if (!relevantPayout) {
      return;
    }
    const { response: csvResponse, error } = await api.safePost(
      'payments/download-payout',
      {
        payout: relevantPayout,
        accountId: activeOrganization.data().accountId
      }
    );

    if (error || !csvResponse) {
      logAndCaptureException(
        ColumnService.PAYMENTS,
        error,
        'An error occurred when downloading payout',
        {
          accountId: activeOrganization.data().accountId,
          payoutId: relevantPayout.id
        }
      );
      return;
    }

    const { csvRows } = csvResponse;
    await Promise.all(csvRows).then((results: any) => {
      const csvContent = makeCsvContent(PAYOUT_CSV_HEADERS, results);
      const arrivalDate = unixTimeStampToNewspaperTimezoneDate(
        relevantPayout.arrival_date,
        activeOrganization.data()?.iana_timezone
      );

      downloadFileContentsInBrowser(
        `Payout_${moment(arrivalDate).format(
          `YYYY-MM-DD_${relevantPayout.id.slice(-5)}`
        )}.csv`,
        csvContent,
        'text/csv'
      );
    });
  };

  /**
   * Fetches balance transactions for any of the given payouts we haven't
   * already cached, and stores the enriched payouts in the cache by id.
   */
  const cacheBalanceTransactionsForPage = async (
    pagePayouts: EPayoutData[]
  ) => {
    if (!exists(activeOrganization)) {
      return;
    }

    const cache = balanceTransactionCacheRef.current;
    const payoutsNeedingTransactions = pagePayouts.filter(
      payout => !cache[payout.id]
    );
    if (!payoutsNeedingTransactions.length) {
      return;
    }

    const { response, error } = await api.safePost(
      'payments/stripe-bt-payouts-data',
      {
        accountId: activeOrganization.data().accountId,
        payouts: payoutsNeedingTransactions
      }
    );

    // The endpoint returns one enriched payout per input payout, in order.
    const enrichedPayouts: EPayoutData[] | undefined =
      response?.balanceTransactions;
    if (error || !enrichedPayouts?.length) {
      return;
    }

    payoutsNeedingTransactions.forEach((payout, index) => {
      const enrichedPayout = { ...payout, ...enrichedPayouts[index] };
      // Only cache successful lookups so transient failures retry on revisit.
      if (payoutHasBalanceTransactionData(enrichedPayout)) {
        cache[payout.id] = enrichedPayout;
      }
    });
  };

  /**
   * Builds the parent/child rows for the current page of payouts, pulling
   * balance transactions from (and populating) the cache as needed.
   */
  const loadCurrentPage = async () => {
    if (!exists(activeOrganization) || !payouts.length) {
      return;
    }

    const requestId = ++pageBuildRequestRef.current;
    setPayoutsLoading(true);

    const pagePayouts = filteredPayouts.slice(
      pageStartIndex,
      pageStartIndex + PAGE_SIZE
    );
    await cacheBalanceTransactionsForPage(pagePayouts);

    const cache = balanceTransactionCacheRef.current;
    const ianaTimezone = activeOrganization.data()?.iana_timezone;
    const groups = pagePayouts.map(payout =>
      buildPayoutGroupRows(cache[payout.id] ?? payout, ianaTimezone)
    );

    // Bail if a newer page/filter build started while we were awaiting, so we
    // don't clobber it with stale results.
    if (pageBuildRequestRef.current !== requestId) {
      return;
    }
    setPayoutGroups(groups);
    setPayoutsLoading(false);
  };

  /**
   * Loads the full list of payout metadata for the active organization. The
   * per-page detail (balance transactions) is loaded lazily by loadCurrentPage.
   */
  const loadPayouts = async () => {
    const { response, error } = await api.safePost(
      'payments/stripe-payout-data'
    );
    const loadedPayouts: EPayoutData[] | undefined = response?.payouts;
    if (error || !loadedPayouts?.length) {
      setPayouts([]);
      // Nothing to page through - loadCurrentPage won't run, so clear here.
      setPayoutsLoading(false);
      return;
    }
    // Setting payouts triggers loadCurrentPage via the page-build effect.
    setPayouts(loadedPayouts);
  };

  const getAndSetActiveProducts = () => {
    if (!exists(activeOrganization)) {
      return;
    }

    const newspaperModel = getModelFromSnapshot(
      OrganizationModel,
      getFirebaseContext(),
      activeOrganization
    );
    setProductsActive({
      [Product.Notice]: true,
      [Product.Obituary]: newspaperModel.hasAdTypeActive(Product.Obituary),
      [Product.Classified]: newspaperModel.hasAdTypeActive(Product.Classified)
    });
  };

  // Organization changed: reset all payout state and reload from scratch.
  useEffect(() => {
    if (!exists(activeOrganization)) {
      return;
    }
    balanceTransactionCacheRef.current = {};
    setLoginLink(null);
    setStripeInvoicesLink('');
    setUserError('');
    setMissingAccountId(false);
    setPayoutGroups([]);
    setPayouts([]);
    setPageNumber(0);
    setPayoutsLoading(true);

    getAndSetActiveProducts();
    void loadStripeLoginLink();
    void loadStripeInvoicesLink();
    void loadPayouts();
  }, [activeOrganization?.id, activeOrganization?.data()?.accountId]);

  // Build the visible page whenever the payout list, filter, or page changes.
  useEffect(() => {
    void loadCurrentPage();
  }, [filteredPayouts, pageNumber]);

  if (payoutsLoading)
    return (
      <LoadingState
        message="Loading payment data..."
        timeout={180}
        context={{
          location: 'PaymentSettings',
          tags: { accountId: activeOrganization?.data()?.accountId ?? '' },
          service: ColumnService.PAYMENTS
        }}
      />
    );

  return (
    <SettingsPage>
      {userError && (
        <Alert status="error" id="payouts-error" title={userError} />
      )}
      <SettingsHeader
        header="Payouts"
        description="Expand to see a breakdown of each payout to your bank."
      >
        <div className="space-x-2 flex items-center">
          {stripeInvoicesLink && (
            <ColumnButton
              tertiary
              id="stripe-invoices"
              size="lg"
              buttonText={'Invoices'}
              onClick={() => window.open(stripeInvoicesLink)}
              startIcon={<CreditCardIcon className="w-5 mr-1" />}
              type="button"
            />
          )}

          {stripeLoginLink && (
            <ColumnButton
              tertiary
              id="stripe-update-account"
              size="lg"
              buttonText={'Update Account'}
              onClick={() => window.open(stripeLoginLink)}
              startIcon={<ArrowTopRightOnSquareIcon className="w-5" />}
              type="button"
            />
          )}
          {(productsActive[Product.Classified] ||
            productsActive[Product.Obituary]) && (
            /* Do not show button to open PayoutsFilterDialog if newspaper
            does not have classifieds or obits active  */
            <button
              type="button"
              onClick={() => setShowFilterMenu(true)}
              className={
                'border-column-gray-200 bg-white border focus:outline-none hover:border-column-primary-400 p-2 px-4 rounded-md text-column-gray-400 font-semibold items-center inline-flex -ml-2'
              }
              id="filter-payouts-menu"
              aria-expanded="true"
              aria-haspopup="true"
            >
              <span className="">Filter</span>
              <ChevronDownIcon className="ml-2 w-5 h-5" />
            </button>
          )}

          {showFilterMenu && (
            <div className="relative inline-block text-left text-column-gray-400">
              <PayoutsFilterDialog
                className="absolute top-2 -ml-2"
                value={payoutsFilterValue}
                onChange={v => {
                  setPayoutsFilterValue(v);
                  setPageNumber(0);
                  setShowFilterMenu(false);
                }}
                productsActive={productsActive}
              />
            </div>
          )}

          <ColumnButton
            tertiary
            id="download-report"
            size="lg"
            buttonText={'Download Report'}
            onClick={() => setOpenDownloadModal(true)}
            startIcon={
              <ArrowDownTrayIcon className="w-5 text-column-primary-900" />
            }
            type="button"
          />
        </div>
      </SettingsHeader>
      <table className="min-w-full divide-y divide-gray-200">
        <thead>
          <tr>
            <PayoutTableHeader />
            <PayoutTableHeader header="amount" />
            <PayoutTableHeader width="1/5" />
            <PayoutTableHeader header="created" />
            <PayoutTableHeader header="arrives/arrived" />
            <PayoutTableHeader header="actions" />
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 rounded-b-lg">
          {payoutGroups.map(group => (
            <PayoutGroupRows
              key={group.parent.id}
              group={group}
              payoutSource={'stripe'}
              handleDownloadCsvClicked={id => downloadCSV(id)}
            />
          ))}
        </tbody>
      </table>
      <footer className="pr-5 py-0.5 bg-column-gray-25 border border-column-gray-100">
        <nav className="px-4 flex items-center justify-between sm:px-0">
          <div className="pl-6 sm:block flex-1 flex">
            <p className="text-sm text-column-gray-400 font-medium mx-1">
              {filteredPayouts.length > PAGE_SIZE
                ? `Showing ${pageStartIndex + 1} to ${
                    pageStartIndex + payoutGroups.length
                  } of ${filteredPayouts.length} payouts`
                : 'Showing all payouts'}
            </p>
          </div>
          <div className="-mt-px w-0 flex-1 flex justify-end">
            <ReactPaginate
              previousLabel={<ChevronLeftIcon className="w-5" />}
              previousClassName="focus:outline-none border-b-2 border-transparent py-4 inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700"
              nextLabel={<ChevronRightIcon className="w-5" />}
              nextClassName="focus:outline-none border-b-2 border-transparent py-4 inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700"
              breakLabel={'...'}
              initialPage={pageNumber}
              forcePage={pageNumber}
              breakClassName="focus:outline-none border-b-2 border-transparent py-4 inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700"
              pageClassName="focus:outline-none border-b-2 border-transparent py-4 inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700 hover:border-gray-300"
              activeLinkClassName="focus:outline-none outline-none text-blue-500 border-blue-500"
              pageLinkClassName="px-4 text-sm"
              pageCount={Math.ceil(filteredPayouts.length / PAGE_SIZE)}
              marginPagesDisplayed={2}
              pageRangeDisplayed={5}
              onPageChange={pageTo => {
                setPageNumber(pageTo.selected);
              }}
              containerClassName={'pagination flex'}
              activeClassName={'text-blue-500 border-blue-500 outline-none'}
            />
          </div>
        </nav>
      </footer>
      {openDownloadModal && (
        <PayoutsDownloadModal close={() => setOpenDownloadModal(false)} />
      )}
    </SettingsPage>
  );
}
